import { useState, useEffect } from "react";

import * as Sentry from "@sentry/react";

import { useTheme } from "@mui/material";

import { YesNoQuestion } from "@aclymatepackages/modules";
import { MultiPartFormLayout } from "@aclymatepackages/multi-part-form";
import mainTheme, { mergeDarkTheme } from "@aclymatepackages/themes";
import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import {
  productMaterials,
  productPackagingMaterials
} from "@aclymatepackages/lists";
import {
  isProductElectronic,
  calculateProductFootprintBreakdown
} from "@aclymatepackages/products";

import ProductCategories from "./ProductCategories";
import ProductSubcategories from "./ProductSubcategories";
import ProductsRouteInput from "./ProductRouteInput";
import ProductWeightInputRow from "./ProductWeightInputRow";
import ProductMaterials from "./ProductMaterialsAutocomplete";
import ProductPackagingMaterials from "./ProductPackagingMaterials";
import MaterialsInterface from "./MaterialsInterface";
import ComponentsInterface from "./ComponentsInterface";

import Backdrop from "../../atoms/mui/Backdrop";
import NumberInput from "../../atoms/inputs/NumberInput";

import { useCachedFirebaseCrud } from "../../../helpers/firebase";
import { getAccountId } from "../../../helpers/accountHelpers";
import {
  isRouteComplete,
  areMaterialsValid,
  areComponentsValid,
  getProductSubcategoryElectonicDefaultWeightPercentage,
  saveProductWithComponents,
  resolveReportSummarySafely,
  isReportSummaryUsable
} from "../../../helpers/components/products";
import { useFetchProductReportSummary } from "../../../helpers/utils/apiCalls";
import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";

const useBasicInformationForm = (existingProducts) => {
  const [basicProductInformation, setBasicProductInformation] = useState({});
  const [productWeights, setProductWeights] = useState({
    productWeight: null,
    packagingWeight: null,
    grossWeight: null
  });
  const [productWeightUnit, setProductWeightUnit] = useState("g");
  const { category, subcategory } = basicProductInformation;

  const editBaseProductObj = (field) => (value) =>
    editObjectData(setBasicProductInformation, field, value);

  const validateUniqueInput = (field, value) =>
    !existingProducts.find((product) => product[field] === value);

  const form = {
    name: "basicInfo",
    label: "Basic Information",
    title: "Enter Basic Product Information"
  };

  const validatedFields = [
    { field: "name", label: "What is this product's name?" },
    {
      field: "sku",
      label: "What is this product's SKU (or other identifier)?"
    }
  ];

  const validatedRows = validatedFields.map(({ field, label }) => {
    const value = basicProductInformation[field];
    return {
      label,
      value,
      helperText:
        value && !validateUniqueInput(field, value)
          ? `A product with this ${field} already exists. Please choose a different ${field}.`
          : "",
      error: value && !validateUniqueInput(field, value),
      editData: editBaseProductObj(field),
      form: "basicInfo"
    };
  });

  const rows = [
    ...validatedRows,
    {
      label: "Select your product's category",
      value: !!category,
      input: (
        <ProductCategories
          value={category}
          setValue={editBaseProductObj("category")}
        />
      ),
      form: "basicInfo"
    },
    {
      label: "Select your product's subcategory",
      value: !!subcategory,
      input: (
        <ProductSubcategories
          category={category}
          value={subcategory}
          setValue={editBaseProductObj("subcategory")}
        />
      ),
      form: "basicInfo"
    },
    {
      value: Object.values(productWeights).every(
        (weight) => !!weight && weight > 0
      ),
      form: "basicInfo",
      input: (
        <ProductWeightInputRow
          weightObject={productWeights}
          setWeightObject={setProductWeights}
          weightUnit={productWeightUnit}
          setWeightUnit={setProductWeightUnit}
        />
      )
    }
  ];

  const numberProductWeights = Object.fromEntries(
    Object.entries(productWeights).map(([key, value]) => [key, Number(value)])
  );

  return {
    form,
    rows,
    basicProductInformation,
    productWeights: numberProductWeights,
    productWeightUnit
  };
};

const useRoutesForm = () => {
  const [route, setRoute] = useState({
    destinations: [{}, {}],
    legs: [{ transportationMethod: "" }]
  });

  const editRoute = (updateObj) =>
    setRoute((currentRoute) => ({ ...currentRoute, ...updateObj }));

  const form = {
    name: "routes",
    label: "Transportation Routes",
    title: "Define Transportation Routes"
  };

  const rows = [
    {
      label: "Define routes and transportation methods",
      value: isRouteComplete(route),
      input: <ProductsRouteInput route={route} editRoute={editRoute} />,
      form: "routes"
    }
  ];

  return { form, rows, route };
};

const useMaterialsInputForm = ({
  isElectronic,
  productWeights,
  form,
  productWeightUnit,
  subcategory
}) => {
  const { productWeight, packagingWeight } = productWeights;
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    const setElectronicsMaterials = () => {
      if (
        isElectronic &&
        form === "materials" &&
        !materials.find(({ isElectronics }) => isElectronics)
      ) {
        return setMaterials((currentMaterials) => [
          {
            type: "li-ion", // Default battery type for electronics in IPPAG sheet
            label: "Li-ion Battery",
            weightUnit: productWeightUnit,
            isElectronics: true
          },
          {
            type: "electronics",
            label: "Electronics",
            weightUnit: productWeightUnit,
            isElectronics: true
          },
          ...currentMaterials
        ]);
      }
    };

    setElectronicsMaterials();
  }, [isElectronic, productWeightUnit, materials, setMaterials, form]);

  const totalWeight = Number(
    form === "materials" ? productWeight : packagingWeight
  );

  const onAddMaterial = (value) => {
    const nonElectronicMaterials = materials.filter(
      ({ isElectronics }) => !isElectronics
    );

    const nonElectronicMaterialsWeightSum = nonElectronicMaterials.reduce(
      (sum, { weight }) => sum + Number(weight || 0),
      0
    );

    const findElectronicsWeight = () => {
      if (!isElectronic || form !== "materials") {
        return 0;
      }

      const defaultElectronicsWeightPercentage =
        getProductSubcategoryElectonicDefaultWeightPercentage(subcategory);

      const defaultElectronicsWeight =
        defaultElectronicsWeightPercentage * totalWeight;

      const electronicsMaterials = materials.filter(
        ({ isElectronics }) => isElectronics
      );

      const electronicsWeightSum = electronicsMaterials.reduce(
        (sum, { weight }) => sum + Number(weight || 0),
        0
      );

      if (electronicsMaterials.every(({ weight }) => weight)) {
        return electronicsWeightSum;
      }

      return defaultElectronicsWeight - electronicsWeightSum;
    };

    const electronicsWeight = findElectronicsWeight();

    const remainingWeight =
      totalWeight &&
      totalWeight > nonElectronicMaterialsWeightSum + electronicsWeight
        ? totalWeight - nonElectronicMaterialsWeightSum - electronicsWeight
        : 0;

    const nonElectronicMaterialsWithoutWeights = nonElectronicMaterials.filter(
      ({ weight }) => !weight
    );
    const estimatedWeightMaterialsCount =
      nonElectronicMaterialsWithoutWeights.length + 1;

    const newMaterialType = value[value.length - 1];

    const materialsList =
      form === "materials" ? productMaterials : productPackagingMaterials;

    const { label } =
      materialsList.find(({ id }) => id === newMaterialType) || {};

    const defaultWeight =
      estimatedWeightMaterialsCount > 0
        ? remainingWeight / estimatedWeightMaterialsCount
        : 0;

    const newMaterialObj = {
      type: newMaterialType,
      defaultWeight,
      weightUnit: productWeightUnit,
      label
    };

    return setMaterials((currentMaterials) => {
      const updatedCurrentMaterials = currentMaterials.map((material) => {
        const { weight, isElectronics } = material;

        if (weight) {
          return material;
        }

        const defaultWeight = isElectronics
          ? electronicsWeight / 2
          : remainingWeight / estimatedWeightMaterialsCount;

        return {
          ...material,
          defaultWeight
        };
      });

      return [...updatedCurrentMaterials, newMaterialObj];
    });
  };

  const onRemoveMaterial = ({ id }) =>
    setMaterials((currentMaterials) =>
      currentMaterials.filter(({ type }) => type !== id)
    );

  const materialSelectProps = {
    multiple: true,
    value: materials.map(({ type }) => type),
    setValue: onAddMaterial,
    onDelete: onRemoveMaterial,
    label: `Select all the ${
      form === "packaging" ? "packaging " : ""
    }materials used in this product`
  };

  const rows = [
    {
      value: !!materials.length,
      input:
        form === "materials" ? (
          <ProductMaterials {...materialSelectProps} />
        ) : (
          <ProductPackagingMaterials {...materialSelectProps} />
        ),
      form
    },
    {
      value: areMaterialsValid(materials, totalWeight),
      input: (
        <MaterialsInterface
          items={materials}
          setItems={setMaterials}
          productWeightUnit={productWeightUnit}
          type={form}
          isElectronic={isElectronic}
          totalWeight={totalWeight}
          noLengthEdit
        />
      ),
      form
    }
  ];

  return { rows, materials };
};

const useComponentsForm = ({
  isElectronic,
  productWeights,
  productWeightUnit
}) => {
  const { productWeight } = productWeights;
  const [components, setComponents] = useState([
    {
      localId: crypto.randomUUID(),
      firestoreId: null,
      name: "",
      materials: [],
      productWeight: 0
    }
  ]);

  const form = {
    name: "components",
    label: "Components",
    title: "Define Product Components"
  };

  const rows = [
    {
      value: areComponentsValid(components, productWeight),
      input: (
        <ComponentsInterface
          components={components}
          setComponents={setComponents}
          productWeightUnit={productWeightUnit}
          totalProductWeight={productWeight}
          isElectronic={isElectronic}
        />
      ),
      form: "components"
    }
  ];

  return { form, rows, components };
};

const usePackagingForm = (productWeights, productWeightUnit) => {
  const { packagingWeight } = productWeights;
  const { rows: materialsRows, materials: packagingMaterials } =
    useMaterialsInputForm({
      productWeights,
      form: "packaging",
      productWeightUnit
    });

  const [knowsPackagingMaterials, setKnowsPackagingMaterials] =
    useState(undefined);

  const DEFAULT_PACKAGING_MATERIALS = [
    {
      label: "Box",
      type: "box",
      weight: packagingWeight / 2,
      weightUnit: productWeightUnit
    },
    {
      label: "Polybag",
      type: "individual_polybag",
      weight: packagingWeight / 2,
      weightUnit: productWeightUnit
    }
  ];

  const form = {
    name: "packaging",
    label: "Packaging",
    title: "Define Product Packaging"
  };

  const rows = [
    {
      NextStepInput: YesNoQuestion,
      nextStepInputProps: {
        question:
          "Do you know the specific materials your product is packaged in? (NOTE: If you don't know specifics about your packaging, we'll use conservative default assumptions instead.",
        value: knowsPackagingMaterials,
        onInputSelect: setKnowsPackagingMaterials,
        inputSelectPropName: "setValue"
      },
      value:
        knowsPackagingMaterials === true || knowsPackagingMaterials === false,
      form: "packaging"
    },
    ...(knowsPackagingMaterials ? materialsRows : [])
  ];

  const packaging = packagingMaterials.length
    ? packagingMaterials
    : DEFAULT_PACKAGING_MATERIALS;

  return {
    form,
    rows,
    packaging
  };
};

const useElectronicsForm = () => {
  const [hasUsageEstimate, setHasUsageEstimate] = useState(undefined);
  const [utilizationTimes, setUtilizationTimes] = useState(null);
  const [knowsBatterySpecs, setKnowsBatterySpecs] = useState(undefined);
  const [kwhConsumedDuringUse, setKwhConsumedDuringUse] = useState(null);
  const [capacityMAh, setCapacityMAh] = useState(null);
  const [voltage, setVoltage] = useState(null);
  const [chargeEfficiencyPercentage, setChargeEfficiencyPercentage] =
    useState(null);
  const [dischargeEfficiencyPercentage, setDischargeEfficiencyPercentage] =
    useState(null);

  const buildUsageEstimateRows = () => [
    {
      NextStepInput: YesNoQuestion,
      nextStepInputProps: {
        question:
          "Do you have an estimate of how many times the product will be used?",
        value: hasUsageEstimate,
        onInputSelect: setHasUsageEstimate,
        inputSelectPropName: "setValue"
      },
      value: hasUsageEstimate === true || hasUsageEstimate === false,
      form: "electronics"
    }
  ];

  const buildUtilizationTimesRows = () =>
    hasUsageEstimate === true
      ? [
          {
            label:
              "How many times will the battery be charged during the product's life?",
            value: utilizationTimes !== null,
            input: (
              <NumberInput
                label="How many times will the battery be charged during the product's life?"
                value={utilizationTimes}
                setValue={setUtilizationTimes}
              />
            ),
            form: "electronics"
          }
        ]
      : [];

  const buildBatterySpecsRows = () => [
    {
      NextStepInput: YesNoQuestion,
      nextStepInputProps: {
        question: "Do you know specification information for the battery?",
        value: knowsBatterySpecs,
        onInputSelect: setKnowsBatterySpecs,
        inputSelectPropName: "setValue"
      },
      value: knowsBatterySpecs === true || knowsBatterySpecs === false,
      form: "electronics"
    },
    ...(knowsBatterySpecs === true
      ? [
          {
            label: "What is the capacity? (mAh)",
            value: capacityMAh !== null,
            input: (
              <NumberInput
                label="What is the capacity? (mAh)"
                value={capacityMAh}
                setValue={setCapacityMAh}
              />
            ),
            form: "electronics"
          },
          {
            label: "What is the voltage? (V)",
            value: voltage !== null,
            input: (
              <NumberInput
                label="What is the voltage? (V)"
                value={voltage}
                setValue={setVoltage}
              />
            ),
            form: "electronics"
          },
          {
            label: "What is the charge efficiency? (%)",
            value: chargeEfficiencyPercentage !== null,
            input: (
              <NumberInput
                label="What is the charge efficiency? (%)"
                value={chargeEfficiencyPercentage}
                setValue={setChargeEfficiencyPercentage}
              />
            ),
            form: "electronics"
          },
          {
            label: "How much energy is consumded during use? (kWh)",
            value: kwhConsumedDuringUse !== null,
            input: (
              <NumberInput
                label="How much energy is consumded during use? (kWh)"
                value={kwhConsumedDuringUse}
                setValue={setKwhConsumedDuringUse}
              />
            ),
            form: "electronics"
          },
          {
            label: "What is the discharge efficiency? (%)",
            value: dischargeEfficiencyPercentage !== null,
            input: (
              <NumberInput
                label="What is the discharge efficiency? (%)"
                value={dischargeEfficiencyPercentage}
                setValue={setDischargeEfficiencyPercentage}
              />
            ),
            form: "electronics"
          }
        ]
      : [])
  ];

  const conditionalRows = [
    ...buildUsageEstimateRows(),
    ...buildUtilizationTimesRows(),
    ...buildBatterySpecsRows()
  ];

  const form = {
    name: "electronics",
    label: "Electronics",
    title: "Define Electronic Specifications"
  };

  const electronics = {
    utilizationTimes,
    voltage,
    capacityMAh,
    chargeEfficiencyPercentage,
    dischargeEfficiencyPercentage,
    kwhConsumedDuringUse
  };

  return { form, rows: conditionalRows, electronics };
};

const NewProductForm = ({ setShowForm, existingProducts }) => {
  const { palette } = useTheme();
  const { newCollectionDoc, updateCollectionDoc } = useCachedFirebaseCrud();
  const [{ emissionsFactors }] = useProductsEmissionsFactors();
  const fetchProductReportSummary = useFetchProductReportSummary();

  const {
    form: basicInformationForm,
    rows: basicInformationRows,
    basicProductInformation,
    productWeights,
    productWeightUnit
  } = useBasicInformationForm(existingProducts);
  const { grossWeight, packagingWeight, productWeight } = productWeights;

  const { name, sku, category, subcategory } = basicProductInformation || {};

  const isElectronic = isProductElectronic(category, subcategory);

  const { form: routesForm, rows: routesRows, route } = useRoutesForm();
  const {
    form: componentsForm,
    rows: componentsRows,
    components
  } = useComponentsForm({
    isElectronic,
    productWeights,
    productWeightUnit
  });

  const {
    form: packagingForm,
    rows: packagingRows,
    packaging
  } = usePackagingForm(productWeights, productWeightUnit);

  const {
    form: electronicsForm,
    rows: electronicsRows,
    electronics
  } = useElectronicsForm();

  const onFormSubmit = async () => {
    const convertMaterialsDefaultWeights = (materials) =>
      materials.map(({ weight, defaultWeight, ...otherProps }) => ({
        ...otherProps,
        weight: Number(weight ?? defaultWeight ?? 0)
      }));

    const accountId = getAccountId();
    const isVendorContext = window.location.pathname.startsWith("/explorer");

    const productLifecycleEventDoc = {
      ownerId: accountId,
      route,
      packaging: convertMaterialsDefaultWeights(packaging),
      packagingWeight,
      grossWeight,
      packagingWeightUnit: productWeightUnit,
      sku
    };

    if (isVendorContext) {
      const productDocObj = {
        creatorId: accountId,
        buyerIds: [],
        vendorIds: [accountId],
        name,
        nameLowercase: name.toLowerCase(),
        materials: components.flatMap((comp) =>
          convertMaterialsDefaultWeights(comp.materials)
        ),
        category,
        subcategory,
        productWeight,
        productWeightUnit,
        ...(isElectronic ? { electronics } : {}),
        archived: false
      };

      const reportSummary = await resolveReportSummarySafely({
        product: productDocObj,
        lifecycleEvent: productLifecycleEventDoc,
        emissionsFactors,
        calculateFootprint: calculateProductFootprintBreakdown,
        fetchReportSummary: fetchProductReportSummary,
        onError: (summaryError) =>
          Sentry.captureException(summaryError, {
            tags: { feature: "report-summary-fetch" },
            extra: { accountId }
          })
      });

      const productId = await newCollectionDoc(
        "vendor-assigned-products",
        productDocObj
      );
      await newCollectionDoc("product-lifecycle-events", {
        ...productLifecycleEventDoc,
        productId,
        ...(isReportSummaryUsable(reportSummary) ? { reportSummary } : {})
      });
      return setShowForm(false);
    }

    const productDocObj = {
      creatorId: accountId,
      buyerIds: [accountId],
      vendorIds: [],
      name,
      nameLowercase: name.toLowerCase(),
      componentRefs: [],
      category,
      subcategory,
      productWeight,
      productWeightUnit,
      ...(isElectronic ? { electronics } : {}),
      archived: false
    };

    await saveProductWithComponents({
      components,
      buildComponentDoc: (comp) => ({
        name: comp.name,
        materials: convertMaterialsDefaultWeights(comp.materials),
        productWeight: comp.productWeight,
        productWeightUnit,
        creatorId: accountId,
        buyerIds: [accountId],
        parentProductId: null,
        archived: false
      }),
      buildMergedComponents: (comps) =>
        comps.map((comp) => ({
          ...comp,
          materials: convertMaterialsDefaultWeights(comp.materials),
          productWeightUnit
        })),
      productDocObj,
      productLifecycleEventDoc,
      fetchReportSummary: (product, lifecycleEvent) =>
        resolveReportSummarySafely({
          product,
          lifecycleEvent,
          emissionsFactors,
          calculateFootprint: calculateProductFootprintBreakdown,
          fetchReportSummary: fetchProductReportSummary,
          onError: (summaryError) =>
            Sentry.captureException(summaryError, {
              tags: { feature: "report-summary-fetch" },
              extra: { accountId }
            })
        }),
      newCollectionDoc,
      updateCollectionDoc,
      onError: (err, { productId, componentIds, lifecycleEventId }) =>
        Sentry.captureException(err, {
          tags: { feature: "add-product-save" },
          extra: {
            accountId,
            productId,
            componentIds,
            lifecycleEventId,
            componentWriteErrors: err?.componentWriteErrors
          }
        })
    });

    return setShowForm(false);
  };

  const convertedForms = [
    basicInformationForm,
    componentsForm,
    packagingForm,
    ...(isElectronic ? [electronicsForm] : []),
    routesForm
  ].map((form) => ({
    ...form,
    size: "lg"
  }));

  const convertedRows = [
    ...basicInformationRows,
    ...componentsRows,
    ...packagingRows,
    ...(isElectronic ? electronicsRows : []),
    ...routesRows
  ];

  return (
    <Backdrop
      style={{ overflowY: "auto" }}
      boxStyle={{ width: "768px", overflowY: "auto", mb: 4 }}
      onClose={() => setShowForm(false)}
    >
      <MultiPartFormLayout
        color={palette.primary.main}
        title="Create A New Product"
        subtitle="Enter your product's information to calculate carbon its footprint"
        onFormSubmit={onFormSubmit}
        forms={convertedForms}
        rows={convertedRows}
        submitLoadingText="Saving product..."
        formSubmitText="Save Product"
        theme={mergeDarkTheme}
        buttonTheme={mainTheme}
        allowFormScrolling
      />
    </Backdrop>
  );
};

export default NewProductForm;
